const { types, referenceTypes, uiTypes } = require("../utils/types");
const { validatorTypes } = require("../utils/validatorTypes");

// One row per deal: the card shown on /deals and the home page, plus the
// optional detail page at /deals/<slug> (filled in when has_detail_page is on).

const editable = (type, extra = {}) => ({
  type,
  validation: [],
  list: false,
  read: true,
  create: true,
  edit: true,
  ...extra,
});

const text = (name, extraUi = {}, required = false) => ({
  name,
  type: types.STRING,
  allowNull: !required,
  validation: [
    validatorTypes.string,
    required ? validatorTypes.required : validatorTypes.optional,
  ],
  ui: editable(uiTypes.TEXT, {
    validation: required ? [validatorTypes.string, validatorTypes.required] : [],
    ...extraUi,
  }),
});

const textArea = (name) => ({
  name,
  type: types.TEXT,
  allowNull: true,
  validation: [validatorTypes.string, validatorTypes.optional],
  ui: editable(uiTypes.TEXT_AREA),
});

const number = (name) => ({
  name,
  type: types.INTEGER,
  allowNull: true,
  validation: [validatorTypes.isInteger, validatorTypes.optional],
  ui: editable(uiTypes.NUMBER),
});

const toggle = (name, defaultValue, extraUi = {}) => ({
  name,
  type: types.BOOLEAN,
  defaultValue,
  validation: [validatorTypes.bool, validatorTypes.optional],
  ui: editable(uiTypes.SWITCH, extraUi),
});

const image = (name) => ({
  name,
  type: types.JSON,
  isArray: false,
  allowNull: true,
  validation: [validatorTypes.json, validatorTypes.optional],
  ui: editable(uiTypes.DOCUMENT_ALT),
});

const repeater = (name, fields) => ({
  name,
  type: types.JSON,
  isArray: true,
  allowNull: true,
  validation: [validatorTypes.array, validatorTypes.optional],
  ui: editable(uiTypes.DYNAMICJSON, { dynamicArrayfeilds: fields }),
});

const obj = {
  tableName: "deals",
  read: true,
  create: true,
  edit: true,
  delete: true,
  comments: false,
  references: [
    {
      tableName: "deal_categories",
      foreignKey: "deal_category_id",
      primaryKey: "id",
      type: referenceTypes.MANY,
      displayKey: "name",
    },
  ],
  columns: [
    {
      name: "id",
      type: types.UUID,
      allowNull: false,
      defaultValue: types.UUIDV4,
      primaryKey: true,
      validation: [validatorTypes.uuid, validatorTypes.optional],
      ui: {
        type: uiTypes.TEXT,
        validation: [],
        list: false,
        read: true,
        create: false,
        edit: false,
      },
    },

    // ---- Card (deals page + home page) ----
    text("title", { list: true }, true),
    text("slug", { list: true }, true),
    {
      name: "deal_category_id",
      type: types.UUID,
      allowNull: true,
      validation: [validatorTypes.uuid, validatorTypes.optional],
      ui: editable(uiTypes.DROPDOWN, { list: true }),
    },
    image("display_image"),
    text("card_tag"),
    text("savings_text"),
    text("price", { list: true }),
    text("list_price"),
    text("location"),
    text("city"),
    number("beds"),
    number("baths"),
    text("area"),
    number("priority"),
    toggle("enabled", true, { list: true }),

    // ---- Detail page (/deals/<slug>) ----
    toggle("has_detail_page", false, { list: true }),
    text("reference_id"),
    text("rera_number"),
    text("window_text"),
    repeater("badges", [
      { name: "label", type: "text" },
      { name: "icon", type: "icon" },
    ]),
    text("locality_text"),
    textArea("summary"),

    text("offer_discount"),
    text("offer_price"),
    text("offer_unit"),
    text("offer_list_price"),
    number("offer_fill_percent"),
    text("offer_equity"),
    text("offer_carpet_rate"),
    text("activity_text"),

    repeater("gallery", [
      { name: "image", type: "image" },
      { name: "tag", type: "text" },
      { name: "eyebrow", type: "text" },
      { name: "caption", type: "text" },
    ]),

    textArea("specs_intro"),
    repeater("specs", [
      { name: "label", type: "text" },
      { name: "value", type: "text" },
      { name: "note", type: "text" },
    ]),
    text("catalyst_title"),
    textArea("catalyst_text"),
    repeater("assurances", [{ name: "text", type: "text" }]),

    text("economics_zone"),
    text("market_label"),
    text("market_price"),
    text("deal_rate_label"),
    text("deal_price"),
    number("deal_fill_percent"),
    text("savings_amount"),
    text("rent_value"),
    text("rent_note"),
    text("yield_value"),
    text("yield_note"),
    textArea("advisory_text"),

    textArea("amenities_intro"),
    repeater("amenities", [
      { name: "title", type: "text" },
      { name: "text", type: "textarea" },
      { name: "icon", type: "icon" },
    ]),
    repeater("distances", [
      { name: "mins", type: "text" },
      { name: "place", type: "text" },
      { name: "note", type: "text" },
    ]),

    text("enquiry_title"),
    textArea("enquiry_text"),
    repeater("enquiry_points", [{ name: "text", type: "text" }]),
    text("advisor_name"),
    text("advisor_contact"),

    {
      name: "createdAt",
      type: types.DATE,
      allowNull: false,
      defaultValue: null,
      ui: {
        type: uiTypes.DATE,
        validation: [],
        list: true,
        read: true,
        create: false,
        edit: false,
      },
    },
    {
      name: "updatedAt",
      type: types.DATE,
      allowNull: false,
      defaultValue: null,
      ui: {
        type: uiTypes.DATE,
        validation: [],
        list: false,
        read: true,
        create: false,
        edit: false,
      },
    },
    {
      name: "deletedAt",
      type: types.DATE,
      allowNull: true,
      defaultValue: null,
      read: false,
      ui: {
        type: uiTypes.TEXT,
        validation: [],
        list: false,
        read: false,
        create: false,
        edit: false,
      },
    },
  ],
};
// The enquiry intro and "Transaction Catalyst & Seller Mandate" blocks were
// removed from the detail page; keep the columns but hide them in the admin so
// nobody edits copy that isn't shown anywhere.
const RETIRED = [
  "enquiry_title",
  "enquiry_text",
  "enquiry_points",
  "advisor_name",
  "advisor_contact",
  "catalyst_title",
  "catalyst_text",
  "assurances",
];
for (const column of obj.columns) {
  if (RETIRED.includes(column.name)) {
    column.ui = { ...column.ui, list: false, read: false, create: false, edit: false };
  }
}

module.exports = obj;
